import { Button, Space } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SdmModuleSpecsFragment } from '../../../../../api/fragments';
import DrawerWithAutoWidth from '../../../../../components/DrawerWithAutoWidth';
import CreateBankIntegrationForm from './BankIntegrationFrom/CreateBankIntegrationForm';
import UpdateBankIntegrationForm from './BankIntegrationFrom/UpdateBankIntegrationForm';

export type BankIntegrationDrawerProps = {
    visible: boolean;
    onClose: () => void;
    module: SdmModuleSpecsFragment;
    bankId?: string;
};

const BankIntegrationDrawer = ({ bankId, module, visible, onClose }: BankIntegrationDrawerProps) => {
    const { t } = useTranslation(['sdmModuleDetails', 'bankDetails']);

    return (
        <DrawerWithAutoWidth
            extra={
                <Space>
                    <Button onClick={onClose}>{t('sdmModuleDetails:actions.cancel')}</Button>
                    <Button form="bankIntegrationForm" htmlType="submit" type="primary">
                        {t(`sdmModuleDetails:actions.${bankId ? 'update' : 'create'}`)}
                    </Button>
                </Space>
            }
            onClose={onClose}
            title={t(`sdmModuleDetails:bankIntegrations.title.${bankId ? 'update' : 'create'}`)}
            visible={visible}
            destroyOnClose
            ignoreRow
        >
            {!bankId && <CreateBankIntegrationForm module={module} onClose={onClose} />}
            {bankId && <UpdateBankIntegrationForm bankId={bankId} module={module} onClose={onClose} />}
        </DrawerWithAutoWidth>
    );
};

export default BankIntegrationDrawer;

export const useBankIntegrationDrawer = () => {
    const [visible, setVisible] = useState(false);
    const [bankId, setBankId] = useState(null);

    const actions = useMemo(
        () => ({
            open: (bankId?: string) => {
                setVisible(true);
                setBankId(bankId);
            },
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (module: SdmModuleSpecsFragment) => (
            <BankIntegrationDrawer bankId={bankId} module={module} onClose={actions.close} visible={visible} />
        ),
    };
};
